import {
  type ListAskPunttChatsResponse,
  type GetAskPunttChatHistoryResponse,
  type CreateAskPunttChatResponse,
  type SendAskPunttMessageResponse,
  type DeleteAskPunttChatResponse,
  type AbortAskPunttMessageResponse,
  type AskPunttProjectData,
} from "@mg/schemas/src/christo/askPuntt";

import { API_URL } from "../config/env";
import { smartFetch } from "../utils/http";

/**
 * Ask Puntt Service - API client for the Ask Puntt chat feature
 */

export async function listChats(params?: {
  limit?: number;
  offset?: number;
}): Promise<ListAskPunttChatsResponse> {
  const queryParams = new URLSearchParams();
  if (params?.limit != null) queryParams.set("limit", String(params.limit));
  if (params?.offset != null) queryParams.set("offset", String(params.offset));

  const queryString = queryParams.toString();
  const url = `${API_URL}/private/catalyst/ask-puntt/chats${queryString ? `?${queryString}` : ""}`;

  const json = await smartFetch(url, { method: "GET" });
  return json as ListAskPunttChatsResponse;
}

export async function getChatHistory(
  chatId: string,
): Promise<GetAskPunttChatHistoryResponse> {
  const json = await smartFetch(
    `${API_URL}/private/catalyst/ask-puntt/chats/${chatId}`,
    { method: "GET" },
  );
  return json as GetAskPunttChatHistoryResponse;
}

export async function createChat(payload: {
  message: string;
  workflowType?: string;
  attachments?: { key: string; source: string; type: string; name?: string }[];
  enabledConnectors?: string[];
}): Promise<CreateAskPunttChatResponse> {
  const json = await smartFetch(`${API_URL}/private/catalyst/ask-puntt/chats`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
  return json as CreateAskPunttChatResponse;
}

export async function getMessageStatus(
  chatId: string,
  messageId: string,
): Promise<import("@mg/schemas/src/christo/askPuntt").AskPunttMessage> {
  const json = await smartFetch(
    `${API_URL}/private/catalyst/ask-puntt/chats/${chatId}/messages/${messageId}/status`,
    { method: "GET" },
  );
  return json as import("@mg/schemas/src/christo/askPuntt").AskPunttMessage;
}

export async function submitFeedback(
  chatId: string,
  messageId: string,
  feedback: "thumbs_up" | "thumbs_down",
): Promise<{ success: boolean }> {
  const json = await smartFetch(
    `${API_URL}/private/catalyst/ask-puntt/chats/${chatId}/messages/${messageId}/feedback`,
    {
      method: "PATCH",
      body: JSON.stringify({ feedback }),
    },
  );
  return json as { success: boolean };
}

export async function deleteChat(
  chatId: string,
): Promise<DeleteAskPunttChatResponse> {
  const json = await smartFetch(
    `${API_URL}/private/catalyst/ask-puntt/chats/${chatId}`,
    { method: "DELETE" },
  );
  return json as DeleteAskPunttChatResponse;
}

export async function sendMessage(
  chatId: string,
  payload: {
    message: string;
    attachments?: {
      key: string;
      source: string;
      type: string;
      name?: string;
    }[];
    sectionContext?: { id: string; title: string };
  },
): Promise<SendAskPunttMessageResponse> {
  const json = await smartFetch(
    `${API_URL}/private/catalyst/ask-puntt/chats/${chatId}/messages`,
    {
      method: "POST",
      body: JSON.stringify(payload),
    },
  );
  return json as SendAskPunttMessageResponse;
}

export async function updateProjectAction(
  chatId: string,
  action: "override_stage" | "complete_project",
  stageName?: string,
): Promise<{ success: boolean; projectData: AskPunttProjectData }> {
  const json = await smartFetch(
    `${API_URL}/private/catalyst/ask-puntt/chats/${chatId}/project/action`,
    {
      method: "PATCH",
      body: JSON.stringify({ action, stageName }),
    },
  );
  return json as { success: boolean; projectData: AskPunttProjectData };
}

/**
 * Phase 5: Lightweight streaming state polling.
 * Returns only the fields needed during streaming instead of the full chat document.
 */
export async function getMessageStreamingState(
  chatId: string,
  messageId: string,
): Promise<{
  status: string;
  content: string;
  streamingContent: string | null;
  agentSteps: unknown[];
  searchProgress: unknown[];
  canvasContent: unknown | null;
  canvasLoading: boolean;
  canvasChangedSectionIds: string[];
  viewTicketId: string | null;
  suggestedResponses: string[];
  projectData: unknown | null;
  contentBlocks: unknown[] | null;
}> {
  const json = await smartFetch(
    `${API_URL}/private/catalyst/ask-puntt/chats/${chatId}/messages/${messageId}/stream`,
    { method: "GET" },
  );
  return json as Awaited<ReturnType<typeof getMessageStreamingState>>;
}

export async function abortMessage(
  chatId: string,
  messageId: string,
): Promise<AbortAskPunttMessageResponse> {
  const json = await smartFetch(
    `${API_URL}/private/catalyst/ask-puntt/chats/${chatId}/messages/${messageId}/abort`,
    { method: "POST" },
  );
  return json as AbortAskPunttMessageResponse;
}
